/**
 * useAuth - Authentication hook with API integration
 */

import { useCallback } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  LoginRequest,
  RegisterRequest,
  RegisterVerifyRequest,
  IdentifyRequest,
  AuthenticateRequest,
  IdentifyByNameRequest,
  LoginTarget,
  SendOtpRequest,
  VerifyOtpRequest,
} from '@aegislab/shared';
import { useAuthStore } from '@/shared/model';
import { authApi } from '../api/authApi';
import { getApiErrorMessage } from '../../../shared/lib/getApiErrorMessage';
import { cleanupPushSubscription } from '@/shared/lib/pwa';
import { logger } from '../../../shared/lib/logger';

/**
 * Authentication hook
 * Provides multi-step login, register, logout functionality with loading/error states
 */
export const useAuth = () => {
  const { user, isLoading, error, setAuth, clearAuth, setLoading, setError } = useAuthStore();
  const queryClient = useQueryClient();

  const isAuthenticated = !!user;

  // mutation 정의에서 onError 콜백을 의도적으로 제거함:
  // mutateAsync 호출자(useCallback wrapper)의 try/catch에서 단일 chain으로 setError 처리.
  // 양쪽에 핸들러를 두면 react-query의 dual-promise 경로 한쪽이 unhandled로 남아
  // Sentry에 "Unhandled Promise Rejection" 노이즈가 쌓임.

  /** Identify user (Multi-step login Step 1) */
  const identifyMutation = useMutation({
    mutationFn: (data: IdentifyRequest) => authApi.identify(data),
  });

  /** Authenticate with password (Multi-step login Step 2) */
  const authenticateMutation = useMutation({
    mutationFn: (data: AuthenticateRequest) => authApi.authenticate(data),
    onSuccess: (response) => {
      queryClient.clear();
      setAuth(response.user);
    },
  });

  /** Login mutation (single-step) */
  const loginMutation = useMutation({
    mutationFn: (data: LoginRequest) => authApi.login(data),
    onSuccess: (response) => {
      queryClient.clear();
      setAuth(response.user);
    },
  });

  /** 가입 요청 — 주소만 보낸다. 다시 보내기도 같은 요청이다 */
  const registerMutation = useMutation({
    mutationFn: (data: RegisterRequest) => authApi.register(data),
    onSuccess: () => {
      // 가입 접수는 토큰을 주지 않는다 — 인증코드 확인과 팀 배정이 끝나야 로그인된다(87bac89b)
      setError(null);
    },
  });

  /** 가입 확인 — 가입한 주소로 받은 6자리 코드와 이름·비밀번호 */
  const registerVerifyMutation = useMutation({
    mutationFn: (data: RegisterVerifyRequest) => authApi.registerVerify(data),
  });

  /** Identify by legal name (OTP flow Step 1) */
  const identifyByNameMutation = useMutation({
    mutationFn: (data: IdentifyByNameRequest) => authApi.identifyByName(data),
  });

  /** Send OTP to user's email */
  const sendOtpMutation = useMutation({
    mutationFn: (data: SendOtpRequest) => authApi.sendOtp(data),
  });

  /** Verify OTP and authenticate */
  const verifyOtpMutation = useMutation({
    mutationFn: (data: VerifyOtpRequest) => authApi.verifyOtp(data),
    onSuccess: (response) => {
      queryClient.clear();
      setAuth(response.user);
    },
  });

  /** Logout mutation */
  const logoutMutation = useMutation({
    mutationFn: async () => {
      await queryClient.cancelQueries();
      return authApi.logout();
    },
    onSuccess: () => {
      queryClient.clear();
      clearAuth();
    },
    onError: () => {
      // Clear auth anyway on error
      queryClient.clear();
      clearAuth();
    },
  });

  /** Identify user (Step 1 of multi-step login) */
  const identify = useCallback(
    async (email: string) => {
      setError(null);
      try {
        return await identifyMutation.mutateAsync({ email });
      } catch (err) {
        setError(getApiErrorMessage(err));
        throw err;
      }
    },
    [identifyMutation, setError]
  );

  /** Authenticate with password (Step 2 of multi-step login) */
  const authenticate = useCallback(
    async (target: LoginTarget, password: string) => {
      setError(null);
      setLoading(true);
      try {
        await authenticateMutation.mutateAsync({ ...target, password });
      } catch (err) {
        setError(getApiErrorMessage(err));
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [authenticateMutation, setError, setLoading]
  );

  /** Login with email/password (single-step) */
  const login = useCallback(
    async (email: string, password: string) => {
      setError(null);
      setLoading(true);
      try {
        await loginMutation.mutateAsync({ email, password });
      } catch (err) {
        setError(getApiErrorMessage(err));
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [loginMutation, setError, setLoading]
  );

  /** Register new user */
  const register = useCallback(
    async (data: RegisterRequest) => {
      setError(null);
      setLoading(true);
      try {
        await registerMutation.mutateAsync(data);
      } catch (err) {
        setError(getApiErrorMessage(err));
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [registerMutation, setError, setLoading]
  );

  /** 가입 확인 — 맞으면 { verified, awaitingTeam } 를 돌려준다 */
  const registerVerify = useCallback(
    async (data: RegisterVerifyRequest) => {
      setError(null);
      setLoading(true);
      try {
        return await registerVerifyMutation.mutateAsync(data);
      } catch (err) {
        setError(getApiErrorMessage(err));
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [registerVerifyMutation, setError, setLoading]
  );

  /** Logout current user */
  const logout = useCallback(async () => {
    setLoading(true);
    try {
      // 로그아웃(토큰 무효화) 전에 이 기기의 푸시 구독을 정리한다. 서버 구독 삭제가
      // endpoint+userId 매칭이라 토큰이 유효한 동안 호출해야 하며, 빠뜨리면 공유 기기에서
      // 다음 사용자에게 이전 사용자 푸시가 새는 cross-user 유출이 된다. 정리 실패는
      // 로그아웃을 막지 않는다(logout 은 항상 성공으로 간주).
      // 정리가 실패해도 로그아웃은 계속한다 — 유출은 cleanupPushSubscription 안의 브라우저
      // 구독 해제가 막고, 여기서 멈추면 로그아웃 자체가 못 끝난다.
      await cleanupPushSubscription().catch((err) => {
        logger.warn('[auth] 로그아웃 전 푸시 구독 정리 실패', err);
      });
      await logoutMutation.mutateAsync();
    } catch {
      // logoutMutation.onError가 clearAuth/queryClient.clear 처리. 재던지지 않음 — UX상 logout은 항상 성공으로 간주
    } finally {
      setLoading(false);
    }
  }, [logoutMutation, setLoading]);

  /** Identify user by real name (OTP flow Step 1) */
  const identifyByName = useCallback(
    async (username: string) => {
      setError(null);
      try {
        return await identifyByNameMutation.mutateAsync({ username });
      } catch (err) {
        setError(getApiErrorMessage(err));
        throw err;
      }
    },
    [identifyByNameMutation, setError]
  );

  /** Identify user by real name + email (disambiguation) */
  const identifyByEmail = useCallback(
    async (username: string, email: string) => {
      setError(null);
      try {
        return await authApi.identifyByEmail({ username, email });
      } catch (err) {
        setError(getApiErrorMessage(err));
        throw err;
      }
    },
    [setError]
  );

  /** Send OTP to user's email */
  const sendOtp = useCallback(
    async (ticket: string) => {
      setError(null);
      try {
        return await sendOtpMutation.mutateAsync({ ticket });
      } catch (err) {
        setError(getApiErrorMessage(err));
        throw err;
      }
    },
    [sendOtpMutation, setError]
  );

  /** Verify OTP and complete authentication */
  const verifyOtp = useCallback(
    async (ticket: string, code: string) => {
      setError(null);
      setLoading(true);
      try {
        await verifyOtpMutation.mutateAsync({ ticket, code });
      } catch (err) {
        setError(getApiErrorMessage(err));
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [verifyOtpMutation, setError, setLoading]
  );

  /** Clear error */
  const clearError = useCallback(() => {
    setError(null);
  }, [setError]);

  return {
    // State
    user,
    isLoading:
      isLoading ||
      identifyMutation.isPending ||
      authenticateMutation.isPending ||
      loginMutation.isPending ||
      registerMutation.isPending ||
      registerVerifyMutation.isPending ||
      identifyByNameMutation.isPending ||
      sendOtpMutation.isPending ||
      verifyOtpMutation.isPending,
    error,
    isAuthenticated,

    // Actions
    identify,
    authenticate,
    login,
    register,
    registerVerify,
    logout,
    clearError,
    identifyByName,
    identifyByEmail,
    sendOtp,
    verifyOtp,

    // Mutation states for granular control
    isIdentifying: identifyMutation.isPending,
    isAuthenticating: authenticateMutation.isPending,
    isLoggingIn: loginMutation.isPending,
    isRegistering: registerMutation.isPending,
    isIdentifyingByName: identifyByNameMutation.isPending,
    isSendingOtp: sendOtpMutation.isPending,
    isVerifyingOtp: verifyOtpMutation.isPending,
  };
};
